import { useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../components/atoms/button";
import CustomTourProvider from "../../../../components/molecules/customTourProvider";
import { getAssessmentCodingQuestionPageSteps } from "../ui-tour/tourStepsData";
import ZoomControls from "./zoomControls";

import { useAppDispatch, useAppSelector } from "../../../../store/store";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";

import GetHeroIcon from "../../../../utils/getHeroIcons";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import Modal from "@assessment/components/atoms/modal";
import Instructions from "../instructions";
import { saveAnswers } from "../saveAnswers";
import { InstructionsArr } from "@assessment/utils/instructionsArr";
import {
  useAttemptQuestionMutation,
  useSaveCodeMutation,
  useSaveSubjectiveMutation,
  useStartQuestionMutation
} from "@assessment/store/slices/api/assessmentApiSlice";
import Switcher from "@lxp-app/layouts/header/switcher-new";
import { useSelector } from "react-redux";
import AppMetaDataSlice from "@lxp-app/store/slices/appMetaDataSlice";
import { RootState } from "@lxp-app/store/store";
import orgBasedFeatureNameMapping from "@assessment/utils/orgBasedFeatureName";

interface Props {
  assessmentTitle: string;
  sectionTitle: string;
  qpInstruction: string;
  scheduleInstruction: string;
  onToggleQuestionPanel?: () => void;
  isQuestionPanelOpen?: boolean;
}

const QuestionHeader = ({
  assessmentTitle,
  sectionTitle,
  qpInstruction,
  scheduleInstruction,
  onToggleQuestionPanel,
  isQuestionPanelOpen = false
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [saveSubjectiveApi] = useSaveSubjectiveMutation();
  const [startQuestion] = useStartQuestionMutation();
  const [saveCodeAPi] = useSaveCodeMutation();
  const [attemptQuestion] = useAttemptQuestionMutation();

  const user = useAppSelector((state) => state.UserInfoSlice.profile);

  const showProctoringVideoPreview = useAppSelector(
    (state) => state.AssessmentSlice.proctoring.showProctoringVideoPreview
  );

  const videoProctoring = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData?.videoProctoring
  );

  const assessmentData = useAppSelector(
    (state) => state.AssessmentSlice.assessmentData
  );

  const activeQuestionMeta = useAppSelector(
    (store) => store.AssessmentSlice.activeQuestionMeta
  );

  const ongoingSections = assessmentData?.ongoingSections;

  const responses = useAppSelector(
    (store) => store.AssessmentSlice.candidateResponse
  );

  const responsesFib = useAppSelector(
    (store) => store.AssessmentSlice.candidateFibResponse
  );

  const ongoingId = useAppSelector((store) => store.AssessmentSlice.ongoingId);

  const viewHeader: any = useSelector(
    (state: RootState) => state.AppMetaDataSlice.header
  );
  const orgBasedFeatureName =
    orgBasedFeatureNameMapping[(user?.org as string) || "default"] ||
    orgBasedFeatureNameMapping["default"];

  const [showUITour, setShowUITour] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const val = InstructionsArr({
    qpInstruction: qpInstruction,
    scheduleInstruction: scheduleInstruction
  });

  const handleSubmit = async () => {
    if (
      ongoingSections &&
      activeQuestionMeta &&
      activeQuestionMeta?.sectionId
    ) {
      const targetSection = ongoingSections.find(
        (section) => section.sectionId === activeQuestionMeta.sectionId
      );

      await saveAnswers(
        targetSection,
        activeQuestionMeta,
        responses,
        ongoingId,
        dispatch,
        saveSubjectiveApi,
        startQuestion,
        saveCodeAPi,
        attemptQuestion,
        responsesFib
      );
    }
    dispatch(AssessmentSliceActions.mutateShowSubmissionModal(true));
  };

  const handleSave = async () => {
    if (
      ongoingSections &&
      activeQuestionMeta &&
      activeQuestionMeta?.sectionId
    ) {
      const targetSection = ongoingSections.find(
        (section) => section.sectionId === activeQuestionMeta.sectionId
      );
      await saveAnswers(
        targetSection,
        activeQuestionMeta,
        responses,
        ongoingId,
        dispatch,
        saveSubjectiveApi,
        startQuestion,
        saveCodeAPi,
        attemptQuestion,
        responsesFib
      );
      dispatch(AssessmentSliceActions.mutateShowSummaryModal(true));
    }
  };

  return (
    <>
      {videoProctoring && <div className="h-14 max-lg:hidden w-full"></div>}
      {!videoProctoring && (
        <div className="h-14 hidden md:inline-block 2xl:hidden w-full"></div>
      )}

      <div className="flex flex-wrap flex-col gap-4 md:gap-6 lg:gap-10 w-full lg:items-center lg:flex-row lg:justify-between">
        <div className="flex items-center gap-3 md:gap-5 lg:gap-10">
          {/* Sections button */}
          <Button
            data-tour="acq-sectionsBtn"
            iconName="ArrowLeftIcon"
            iconPosition="leading"
            iconVariant="outline"
            variant="outlined"
            buttonColor="secondary"
            onClick={() => handleSave()}
          >
            <span className="max-sm:hidden">
              {t("assessment.question.sections")}
            </span>
          </Button>
          <div className="flex-1 space-y-3 md:space-y-4" data-tour="acq-titles">
            <h1 className="text-lg md:text-xl font-semibold leading-7 md:leading-8 text-secondary text-wrap w-full">
              {assessmentTitle}
            </h1>
            <p className="text-sm md:text-base font-semibold leading-6 text-secondary">
              {sectionTitle}
            </p>
          </div>
        </div>
        {/* Right - Mobile */}
        <div className="flex lg:hidden items-center w-full flex-wrap justify-between gap-2 sm:gap-3">
          <button
            type="button"
            onClick={onToggleQuestionPanel}
            className="flex items-center justify-center px-3 py-2.5 rounded-lg bg-secondary-bg border border-secondary/20 text-secondary hover:bg-secondary/10 transition-colors flex-none"
            aria-label={t("assessment.question.toggleQuestionsPanel")}
            title={t("assessment.question.questions")}
          >
            <GetHeroIcon
              name={isQuestionPanelOpen ? "XMarkIcon" : "Bars3CenterLeftIcon"}
              className="w-5 h-5"
              aria-hidden="true"
              variant="outline"
            />
            <span className="ml-2 text-sm font-medium leading-5">
              {t("assessment.question.questions")}
            </span>
          </button>

          <Popover className="relative inline-block flex-none">
            <PopoverButton className="inline-flex items-center font-semibold p-2 rounded-lg hover:bg-secondary/5 transition-colors">
              <span className="flex items-center gap-1 cursor-pointer">
                <GetHeroIcon
                  name="ExclamationCircleIcon"
                  variant="outline"
                  className="w-5 h-5 text-secondary"
                />
                <span>{t("common.help")}</span>
              </span>
            </PopoverButton>
            <PopoverPanel
              transition
              className="absolute right-0 z-30 mt-2 flex w-screen max-w-min px-2 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
            >
              {({ close }: any) => (
                <div className="w-56 p-3 text-base font-medium shadow-lg shrink rounded-xl bg-bkg text-secondary ring-1 ring-secondary/5">
                  <div
                    className="flex items-center gap-2 my-2 cursor-pointer hover:bg-secondary/5 p-2 rounded transition-colors"
                    onClick={() => {
                      setShowUITour(true);
                      close();
                    }}
                  >
                    <span>
                      <GetHeroIcon
                        name={"CursorArrowRaysIcon"}
                        variant="outline"
                        className="w-5 h-5 text-black dark:text-white"
                      />
                    </span>
                    <span>{t("common.tour")}</span>
                  </div>
                  <hr />
                  <div
                    className="flex items-center gap-2 my-2 cursor-pointer hover:bg-secondary/5 p-2 rounded transition-colors"
                    onClick={() => setOpenModal(!openModal)}
                  >
                    <span>
                      <GetHeroIcon
                        name={"InformationCircleIcon"}
                        variant="outline"
                        className="w-5 h-5 text-black dark:text-white"
                      />
                    </span>
                    <span>{t("common.instructions")}</span>
                  </div>
                </div>
              )}
            </PopoverPanel>
          </Popover>

          <div className="flex-none min-w-fit">
            <ZoomControls compact={false} showLabel={true} />
          </div>

          <Button
            data-tour="acq-submitBtn"
            variant="contained"
            buttonColor="secondary"
            onClick={handleSubmit}
            className="px-4 flex-none max-sm:w-full max-sm:justify-center border-none bg-red-500 hover:bg-red-500 dark:bg-red-700 dark:hover:bg-red-700"
          >
            {t("assessment.dashboard.submitFeature", {
              featureName: orgBasedFeatureName
            })}
          </Button>
        </div>

        {/* Right - Desktop */}
        <div className="hidden lg:flex items-center justify-between flex-shrink-0 w-full gap-8 lg:justify-center lg:w-fit">
          {videoProctoring && (
            <button
              type="button"
              className="flex items-center gap-4 px-3 py-2.5 border rounded-md leading-5"
              onClick={() => {
                if (showProctoringVideoPreview)
                  dispatch(
                    AssessmentSliceActions.mutateShowProctoringVideoPreview(
                      false
                    )
                  );
                else
                  dispatch(
                    AssessmentSliceActions.mutateShowProctoringVideoPreview(
                      true
                    )
                  );
              }}
            >
              <div className="w-2 h-2 rounded-full bg-[#ff5f20] animate-pulse" />
              <span className="flex-1 text-sm font-medium leading-5">
                {t("assessment.dashboard.videoProctoring")}
              </span>
              <GetHeroIcon
                name={
                  showProctoringVideoPreview
                    ? "ArrowsPointingInIcon"
                    : "ArrowsPointingOutIcon"
                }
                className="w-4 h-4"
              />
            </button>
          )}

          {!viewHeader && <Switcher />}

          <div className="flex items-center gap-8">
            <ZoomControls compact={false} showLabel={true} />

            <Popover className="relative inline-block">
              <PopoverButton className="inline-flex items-center font-semibold">
                <span className="flex">
                  <div className="flex items-center gap-1 cursor-pointer">
                    <GetHeroIcon
                      name="ExclamationCircleIcon"
                      variant="outline"
                      className="w-5 h-5 text-secondary"
                    />
                    <span>{t("common.help")}</span>
                  </div>
                </span>
              </PopoverButton>
              <PopoverPanel
                transition
                className="absolute left-1/2 z-30 mt-3 flex w-screen max-w-min -translate-x-3/4 px-4 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
              >
                {({ close }: any) => (
                  <div className="w-56 p-3 text-base font-medium shadow-lg shrink rounded-xl bg-bkg text-secondary ring-1 ring-secondary/5">
                    <div
                      className="flex items-center gap-2 my-2 cursor-pointer"
                      onClick={() => {
                        setShowUITour(true);
                        close();
                      }}
                    >
                      <span>
                        <GetHeroIcon
                          name={"CursorArrowRaysIcon"}
                          variant="outline"
                          className="w-5 h-5 text-black dark:text-white"
                        />
                      </span>
                      <span>{t("common.tour")}</span>
                    </div>
                    <hr />
                    <div
                      className="flex items-center gap-2 my-2 cursor-pointer"
                      onClick={() => setOpenModal(!openModal)}
                    >
                      <span>
                        <GetHeroIcon
                          name={"InformationCircleIcon"}
                          variant="outline"
                          className="w-5 h-5 text-black dark:text-white"
                        />
                      </span>
                      <span>{t("common.instructions")}</span>
                    </div>
                  </div>
                )}
              </PopoverPanel>
            </Popover>

            <Button
              data-tour="acq-submitBtn"
              variant="contained"
              buttonColor="secondary"
              className="bg-red-500 border-none hover:bg-red-500 dark:bg-red-700 dark:hover:bg-red-700"
              onClick={handleSubmit}
            >
              {t("assessment.dashboard.submitFeature", {
                featureName: orgBasedFeatureName
              })}
            </Button>
          </div>
        </div>
      </div>
      <CustomTourProvider
        steps={getAssessmentCodingQuestionPageSteps(t)}
        isTourOpen={showUITour}
        setIsTourOpen={setShowUITour}
      />
      <Modal
        open={openModal}
        children={<Instructions instructionType={val} />}
        setOpen={setOpenModal}
        dialogueClassName="w-fit"
        closeBtn={false}
        childDialogTitle={t("common.instructions")}
      />
    </>
  );
};

export default QuestionHeader;
